import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { LanguageMenu } from "@/components/language-menu";
import { LandingMenu } from "@/features/platform/components/landing-menu";
import { GoLink, fogClass, ghostButton, landingVars } from "@/features/platform/components/landing-ui";
import { BrandMark } from "@/features/site/components/brand-mark";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { appConfig } from "@complete-athlete/enums";

/**
 * The platform's own chrome, as opposed to a trainer's, in the same hand as the athlete app's
 * front page: a head card with the navigation and the fog rising from its bottom edge, the content
 * column, and a footer card. A trainer's chrome lives in features/site and is driven by their slug.
 */

/**
 * The pages in the bar. Every one is a page of its own, never a jump to a section of the landing:
 * a link has to be shareable and land on what it names. The questions trainers ask live on the
 * pricing page and in the help centre.
 */
const pages = () =>
  [
    { to: "/who-its-for", label: m.platform_nav_for_whom() },
    { to: "/features", label: m.platform_nav_features() },
    { to: "/pricing", label: m.platform_nav_pricing() },
    { to: "/blog", label: m.blog_nav() },
    { to: "/help", label: m.help_nav() },
  ] as const;

export const PlatformBrand = ({ className }: { className?: string }) => (
  <Link
    to="/"
    className={cn(
      "group flex items-center gap-2.5 text-lg font-semibold tracking-[-0.02em] whitespace-nowrap",
      className,
    )}
  >
    <BrandMark className="transition-transform duration-500 ease-out group-hover:-rotate-12" />
    {appConfig.name}
  </Link>
);

const navLink = "text-muted-foreground transition-colors hover:text-foreground";

/**
 * The card every platform page opens with: the navigation on top and, on the front page, the hero.
 * Under `lg` the page links move into the menu behind the round button.
 */
export const PlatformHead = ({ children, className }: { children?: ReactNode; className?: string }) => (
  <div
    className={cn(
      "relative isolate overflow-hidden rounded-[32px] border border-border bg-background px-5 pt-5 pb-8 md:px-9 md:pt-[26px] md:pb-11",
      fogClass,
      className,
    )}
  >
    <header className="relative z-10 flex items-center gap-4">
      <PlatformBrand className="flex-1" />
      <nav className="hidden items-center gap-4 text-sm font-medium whitespace-nowrap lg:flex xl:gap-[26px]">
        {pages().map((link) => (
          <Link key={link.to} to={link.to} className={navLink} activeProps={{ className: "text-foreground" }}>
            {link.label}
          </Link>
        ))}
      </nav>
      <div className="flex flex-1 items-center justify-end gap-2">
        <LanguageMenu style={landingVars} className="max-sm:hidden" />
        <Link to="/login" className={cn(ghostButton, "max-sm:hidden")}>
          {m.landing_nav_sign_in()}
        </Link>
        <GoLink to="/signup" tone="accent" className="max-sm:hidden">
          {m.platform_nav_start()}
        </GoLink>
        <LandingMenu
          className="lg:hidden"
          style={landingVars}
          brand={<PlatformBrand />}
          links={[
            ...pages().map((link) => ({ label: link.label, to: link.to })),
            { label: m.landing_nav_contact(), to: "/contact" },
          ]}
          cta={
            <GoLink to="/signup" tone="accent">
              {m.platform_nav_start()}
            </GoLink>
          }
        />
      </div>
    </header>
    {children}
  </div>
);

const columnTitle = "mb-3.5 text-[13px] font-medium text-muted-foreground";
const columnLink = "block py-[5px] text-sm font-medium transition-colors hover:text-muted-foreground";

export const PlatformFooter = () => (
  <footer className="mt-16 rounded-[32px] border border-border bg-background px-6 pt-8 pb-5 md:mt-[104px] md:px-10 md:pt-11 md:pb-[26px]">
    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
      <div>
        <PlatformBrand />
        <p className="mt-3.5 max-w-[280px] text-sm text-muted-foreground">{m.platform_footer_blurb()}</p>
      </div>
      <div>
        <h4 className={columnTitle}>{m.platform_footer_product()}</h4>
        <Link to="/who-its-for" className={columnLink}>
          {m.platform_nav_for_whom()}
        </Link>
        <Link to="/features" className={columnLink}>
          {m.platform_nav_features()}
        </Link>
        <Link to="/pricing" className={columnLink}>
          {m.platform_nav_pricing()}
        </Link>
        <Link to="/how-it-works" className={columnLink}>
          {m.platform_nav_how()}
        </Link>
        <Link to="/blog" className={columnLink}>
          {m.blog_nav()}
        </Link>
      </div>
      <div>
        <h4 className={columnTitle}>{m.platform_footer_account()}</h4>
        <Link to="/signup" className={columnLink}>
          {m.platform_nav_start()}
        </Link>
        <Link to="/login" className={columnLink}>
          {m.landing_nav_sign_in()}
        </Link>
      </div>
      <div>
        <h4 className={columnTitle}>{m.platform_footer_company()}</h4>
        <Link to="/help" className={columnLink}>
          {m.help_title()}
        </Link>
        <Link to="/contact" className={columnLink}>
          {m.landing_nav_contact()}
        </Link>
        <Link to="/privacy" className={columnLink}>
          {m.footer_privacy()}
        </Link>
        <Link to="/terms" className={columnLink}>
          {m.footer_terms()}
        </Link>
        <Link to="/cookies" className={columnLink}>
          {m.footer_cookies()}
        </Link>
        <Link to="/consumer-health-data" className={columnLink}>
          {m.footer_health_data()}
        </Link>
        <Link to="/legal" className={columnLink}>
          {m.footer_legal()}
        </Link>
      </div>
    </div>
    <div className="mt-11 flex flex-col justify-between gap-1.5 border-t border-border pt-5 text-[13px] text-muted-foreground sm:flex-row">
      <span>
        © {new Date().getFullYear()} {appConfig.name}
      </span>
      <span>{m.platform_footer_note()}</span>
    </div>
  </footer>
);

/**
 * One platform page: the head card, the content column and the footer. `head` replaces the bare
 * navigation card when the page has a hero to put inside it.
 */
export const PlatformPage = ({ head, children }: { head?: ReactNode; children: ReactNode }) => (
  <div style={landingVars} className="min-h-svh bg-page-background p-3 text-sm text-foreground">
    <div className="mx-auto max-w-[1416px]">
      {head ?? <PlatformHead />}
      <main>{children}</main>
      <PlatformFooter />
    </div>
  </div>
);
